import { useState, ChangeEvent } from "react";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as RocketIcon } from "../../../assets/rocket-gradient.icon.svg";
import { ReactComponent as CheckCircleIcon } from "../../../assets/check-circle.icon.svg";
import { sendFeedback } from "../../../api/feedback/endpoints-referrers";
import { LoaderStep } from "../../../common/enums/loader-step.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { emailRegex } from "../../../common/utilities/constants/constants";
import { LoaderView } from "../../../components/loader/loader.comp";

const FeedbackView = ({ handleClose }: { handleClose?: () => void }) => {
    const emailMask = new RegExp(process.env.REACT_APP_EMAIL_REGEX ?? emailRegex, "g");
    const { user } = FirebaseAuthContainer.useContainer();
    const [step, setStep] = useState<LoaderStep>(LoaderStep.start);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [organization, setOrganization] = useState<string>("");
    const [job, setJob] = useState<string>("");
    const [message, setMessage] = useState<string>("");

    const handleCloseClick = () => {
        if (handleClose) {
            handleClose();
        }
    };

    const handleFirstNameChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setFirstName(val ?? "");
    };

    const handleLastNameChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLastName(val ?? "");
    };

    const handleEmailChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setEmail(val ?? "");
    };

    const handleOrganizationChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setOrganization(val ?? "");
    };

    const handleJobChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setJob(val ?? "");
    };

    const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const handleSendRequestClick = () => {
        if (!user) return;

        setIsLoading(true);
        sendFeedback(user.companyId, {
            firstName,
            lastName,
            email,
            organization: organization ?? undefined,
            job: job ?? undefined,
            additionalInfo: message ?? undefined
        })
            .then(() => {
                setStep(LoaderStep.complete);
            })
            .catch(() => {
                setStep(LoaderStep.error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div className="feedback">
            <div className="feedback-title">
                <SvgIcon className="feedback-title__icon" component={RocketIcon} inheritViewBox />
                <span
                    className={`feedback-title__text${step === LoaderStep.error ? " feedback-title__text--error" : ""}`}
                    role="heading"
                    aria-level={1}
                >
                    {step === LoaderStep.start && "Upgrade to full version"}
                    {step === LoaderStep.error && "Error"}
                    {step === LoaderStep.complete && "Thank you!"}
                </span>
            </div>
            {step === LoaderStep.start && (
                <>
                    <div className="feedback-description">
                        <span className="feedback-description-header" role="heading" aria-level={2}>
                            What you get with the full version:
                        </span>
                        <div className="feedback-description-body">
                            <div className="feedback-description-body-item">
                                <SvgIcon
                                    className="feedback-description-body-item__icon"
                                    component={CheckCircleIcon}
                                    inheritViewBox
                                />
                                <span className="feedback-description-body-item__text">
                                    Unlimited usage
                                </span>
                            </div>
                            <div className="feedback-description-body-item">
                                <SvgIcon
                                    className="feedback-description-body-item__icon"
                                    component={CheckCircleIcon}
                                    inheritViewBox
                                />
                                <span className="feedback-description-body-item__text">
                                    Unlimited Knowledge Bases
                                </span>
                            </div>
                            <div className="feedback-description-body-item">
                                <SvgIcon
                                    className="feedback-description-body-item__icon"
                                    component={CheckCircleIcon}
                                    inheritViewBox
                                />
                                <span className="feedback-description-body-item__text">
                                    Unlimited docs for upload
                                </span>
                            </div>
                            <div className="feedback-description-body-item">
                                <SvgIcon
                                    className="feedback-description-body-item__icon"
                                    component={CheckCircleIcon}
                                    inheritViewBox
                                />
                                <span className="feedback-description-body-item__text">
                                    External file sources (Google drive, One drive)
                                </span>
                            </div>
                            <div className="feedback-description-body-item">
                                <SvgIcon
                                    className="feedback-description-body-item__icon"
                                    component={CheckCircleIcon}
                                    inheritViewBox
                                />
                                <span className="feedback-description-body-item__text">
                                    Unlimited users
                                </span>
                            </div>
                            <div className="feedback-description-body-item">
                                <SvgIcon
                                    className="feedback-description-body-item__icon"
                                    component={CheckCircleIcon}
                                    inheritViewBox
                                />
                                <span className="feedback-description-body-item__text">
                                    Manager role (user that can add documents to KB)
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="feedback-fields">
                        <div className="feedback-field">
                            <input
                                type="text"
                                aria-label="First Name"
                                placeholder="First Name"
                                onChange={handleFirstNameChange}
                                value={firstName}
                            />
                            <span className="feedback-field__required">*</span>
                        </div>
                        <div className="feedback-field">
                            <input
                                type="text"
                                aria-label="Last Name"
                                placeholder="Last Name"
                                onChange={handleLastNameChange}
                                value={lastName}
                            />
                            <span className="feedback-field__required">*</span>
                        </div>
                        <div className="feedback-field">
                            <input
                                type="text"
                                aria-label="Business Email"
                                placeholder="Business Email"
                                onChange={handleEmailChange}
                                value={email}
                            />
                            <span className="feedback-field__required">*</span>
                        </div>
                        <div className="feedback-field">
                            <input
                                type="text"
                                aria-label="Organization"
                                placeholder="Organization"
                                onChange={handleOrganizationChange}
                                value={organization}
                            />
                        </div>
                        <div className="feedback-field">
                            <input
                                type="text"
                                aria-label="Your Job Title"
                                placeholder="Your Job Title"
                                onChange={handleJobChange}
                                value={job}
                            />
                        </div>
                    </div>
                    <textarea
                        className="feedback-message"
                        aria-label="Write anything you'd like to ask or add"
                        onChange={handleTextChange}
                        placeholder="Write anything you'd like to ask or add"
                        value={message}
                    />
                </>
            )}
            {step === LoaderStep.error && (
                <div className="feedback-description" role="alert">
                    <span className="feedback-description-header">
                        Something went wrong. Please try again
                    </span>
                </div>
            )}
            {step === LoaderStep.complete && (
                <div className="feedback-description">
                    <span className="feedback-description-header">
                        Upgrade Request Sent. We&apos;ll get back to you shortly!
                    </span>
                </div>
            )}
            <div className="feedback-action">
                {step === LoaderStep.start && (
                    <>
                        {handleClose && (
                            <Button
                                className="thirt-button feedback-action__button-margin"
                                variant="outlined"
                                onClick={handleCloseClick}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            className="trial-button"
                            variant="outlined"
                            disabled={!firstName || !lastName || !email || !email.match(emailMask)}
                            onClick={handleSendRequestClick}
                        >
                            Send Request
                        </Button>
                    </>
                )}
                {step === LoaderStep.error && (
                    <Button
                        className="primary-button"
                        variant="outlined"
                        onClick={() => {
                            setStep(LoaderStep.start);
                        }}
                    >
                        Try Again
                    </Button>
                )}
                {step === LoaderStep.complete && handleClose && (
                    <Button
                        className="primary-button"
                        variant="outlined"
                        onClick={handleCloseClick}
                    >
                        Ok
                    </Button>
                )}
            </div>
            <LoaderView show={isLoading} />
        </div>
    );
};

export default FeedbackView;
